import React, { useEffect, useRef, useState } from "react";
import styles from "./Shards.module.css";

/*
 * Shards — the hero's particle field. A slowly turning orb of paint shards
 * wraps the centrepiece, bursts, and the shards settle into a field of
 * drifting flecks across the whole box that a moving cursor shoves aside.
 *
 *   <Shards className={styles.stage}>{centrepiece}</Shards>
 *
 * Always position:absolute, filling its POSITIONED parent, pointer-events:none.
 * Two optional custom properties on the wrapper (unitless shares of the box
 * height) keep the settled field thin where the parent has small UI to read:
 *   --shards-quiet-top     band under a fixed header         (default 0)
 *   --shards-quiet-bottom  band over a row of captions/cards (default 0)
 * Only a few of the smallest flecks that would rest in a quiet band stay there.
 * The settled field also keeps clear of the centrepiece itself (the first
 * element child, plus CLEAR_PAD): shards whose resting spot falls inside it
 * fade out after the burst, so a line of text never collects stray "accents"
 * and "apostrophes" between its letters. The orb still wraps it.
 * `children` are centred in the box BETWEEN two canvases driven by one loop:
 * shards on the near side of the orb (and, once scattered, the few "near" ones)
 * draw on the FRONT canvas, the rest on the BACK one — so the orb wraps the
 * centrepiece instead of sitting behind it.
 *
 * Sequence
 *   intro   the orb grows in once the preloader has gone (html.is-preloading /
 *           html.pre-curtain hold it), turning on a tilted axis inside a ring.
 *   burst   on the first pointer move, touch or scroll — or AUTO_BURST_S after
 *           the intro, so touch devices and a still mouse see it too — every
 *           shard flies outward to its resting spot and settles on a spring.
 *   field   shards drift on slow sine paths, spin, shift with a little depth
 *           parallax, and are pushed aside by a moving cursor / finger.
 *
 * Under prefers-reduced-motion (followed live) it draws ONE still frame of the
 * settled field and never starts a loop. The loop pauses on a hidden tab and
 * while the box is off-screen. Without canvas the centrepiece stands alone.
 *
 * Deterministic: shapes, colours and resting spots come from a seeded PRNG, so
 * every visit (and every capture) composes the same way. Colours are palette
 * tokens read off the wrapper — no raw colour lives in this file.
 */

/* ------------------------------------------------------------ parameters -- */
const SEED = 20261007;
const REDUCED_QUERY = "(prefers-reduced-motion: reduce)";
const PRELOAD_CLASSES = ["is-preloading", "pre-curtain"];
const MAX_DPR = 1.5;

const INTRO_S = 1.1; // orb grow-in
const MIN_ORB_S = 1.0; // the orb is always seen whole for at least this long
const AUTO_BURST_S = 3.2; // ...and bursts on its own after this
const SETTLE_S = 0.9; // size / alpha / layer hand-over after the burst

const ORB_SPIN = 0.32; // rad/s
const ORB_TILT = 0.35; // rad, about X
const ORB_SHARD_SCALE = 1.5; // shards draw larger on the orb, so it reads as a shell
const RING_SHARE = 0.3; // share of the shards that form the ring
const RING_RADIUS = 1.5; // x the orb radius
const RING_TILT = 1.18; // rad, about X — lays the ring nearly flat
const RING_ROLL = -0.42; // rad, about Z — the diagonal
const PERSPECTIVE = 3.2; // camera distance, in orb radii

const SPRING = 7; // 1/s^2
const DAMPING = 4.2; // 1/s  (~0.8 of critical at SPRING)
const KICK = 2.6; // burst velocity, x the distance to the resting spot
const PUSH_REACH = 150; // px at unit scale
const PUSH_BASE = 900; // px/s^2 at the core, cursor still (a ~50px clearing)
const PUSH_SPEED = 7; // + this x cursor speed (px/s)
const PARALLAX = 0.06; // share of the cursor's offset from centre
const CLEAR_PAD = 24; // px at unit scale kept free around the centrepiece

/* Palette tokens and each one's share of the shards. Lime is a fill only and
   always gets an ink edge (the hero's status lamp does the same): on paper a
   bare lime fleck all but disappears. */
const COLOURS = ["--ink", "--olive", "--ink-soft", "--lime-ink", "--lime"];
const WEIGHTS = [0.46, 0.22, 0.12, 0.12, 0.08];
const INK = 0;
const LIME = 4;

/* Drawing is batched: one fill per (layer, colour, opacity step) instead of one
   per shard, so a frame is a dozen or so fills rather than hundreds. */
const ALPHA_STEPS = 10;
const BUCKETS_PER_LAYER = COLOURS.length * (ALPHA_STEPS + 1);

/* ----------------------------------------------------------------- helpers -- */
function canAnimate() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return true;
  }
  return !window.matchMedia(REDUCED_QUERY).matches;
}

function mulberry32(seed) {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function countFor(width) {
  if (width >= 1024) {
    return 300;
  }
  return width >= 640 ? 220 : 150;
}

function easeOutCubic(t) {
  const u = 1 - Math.min(1, Math.max(0, t));
  return 1 - u * u * u;
}

function pickColour(roll) {
  let sum = 0;
  for (let i = 0; i < WEIGHTS.length; i += 1) {
    sum += WEIGHTS[i];
    if (roll < sum) {
      return i;
    }
  }
  return INK;
}

/* One shard: where it sits on the orb, where it rests in the field (0..1 of
   the box), its outline, and how it drifts. */
function buildShards(count) {
  const rand = mulberry32(SEED + count);
  const ringCount = Math.round(count * RING_SHARE);
  const sphereCount = count - ringCount;
  const golden = Math.PI * (3 - Math.sqrt(5));

  // Resting spots: a jittered grid, shuffled, so the field is even rather than
  // clumpy. spotX / spotY are the raw spot; place() folds it out of the quiet
  // bands into rx / ry.
  const cols = Math.ceil(Math.sqrt(count * 1.6));
  const rows = Math.ceil(count / cols);
  const cells = [];
  for (let c = 0; c < cols * rows; c += 1) {
    cells.push(c);
  }
  for (let c = cells.length - 1; c > 0; c -= 1) {
    const j = Math.floor(rand() * (c + 1));
    const swap = cells[c];
    cells[c] = cells[j];
    cells[j] = swap;
  }

  const shards = [];
  for (let i = 0; i < count; i += 1) {
    let ox = 0;
    let oy = 0;
    let oz = 0;
    const ring = i >= sphereCount;
    if (ring) {
      const a = ((i - sphereCount) / ringCount) * Math.PI * 2 + rand() * 0.08;
      const r = RING_RADIUS + (rand() - 0.5) * 0.16;
      ox = Math.cos(a) * r;
      oy = (rand() - 0.5) * 0.08;
      oz = Math.sin(a) * r;
    } else {
      // Fibonacci sphere: even cover, no poles
      const y = 1 - (2 * (i + 0.5)) / sphereCount;
      const r = Math.sqrt(1 - y * y);
      const th = golden * i;
      const shell = 0.92 + rand() * 0.14;
      ox = Math.cos(th) * r * shell;
      oy = y * shell;
      oz = Math.sin(th) * r * shell;
    }

    const cell = cells[i];
    const spotX = ((cell % cols) + 0.15 + rand() * 0.7) / cols;
    const spotY = (Math.floor(cell / cols) + 0.15 + rand() * 0.7) / rows;

    // An irregular 3–5 point sliver, stretched along its own x axis.
    const points = 3 + Math.floor(rand() * 3);
    const stretch = 1 + rand() * 1.8;
    const verts = new Float32Array(points * 2);
    for (let k = 0; k < points; k += 1) {
      const ang = (k / points) * Math.PI * 2 + (rand() - 0.5) * (Math.PI / points);
      const rad = 0.5 + rand() * 0.5;
      verts[k * 2] = Math.cos(ang) * rad * stretch;
      verts[k * 2 + 1] = Math.sin(ang) * rad;
    }

    // Mostly flecks, some chips, a few large translucent slabs.
    const sizeRoll = rand();
    const large = sizeRoll < 0.06;
    let size = 2.5 + rand() * 3.5;
    if (large) {
      size = 15 + rand() * 15;
    } else if (sizeRoll < 0.32) {
      size = 6.5 + rand() * 5;
    }
    const colour = large ? 1 + Math.floor(rand() * 2) : pickColour(rand());
    const depth = rand();
    const stayRoll = rand();

    shards.push({
      ox,
      oy,
      oz,
      ring,
      // only the smallest flecks may stay in a quiet band, and few of them
      spot: { x: spotX, y: spotY, keep: size < 5 && stayRoll < 0.16, alt: rand() },
      rx: spotX,
      ry: spotY,
      verts,
      points,
      size,
      colour,
      alpha: large ? 0.14 + rand() * 0.14 : 0.72 + rand() * 0.28,
      depth,
      // large slabs always pass BEHIND the centrepiece
      front: !large && depth > 0.86,
      angle: rand() * Math.PI * 2,
      spin: (rand() - 0.5) * 0.9,
      kick: (rand() - 0.5) * 14,
      w1: 0.12 + rand() * 0.22,
      w2: 0.1 + rand() * 0.2,
      p1: rand() * Math.PI * 2,
      p2: rand() * Math.PI * 2,
      amp: 8 + depth * 22,
      // live state, in px of the box
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
      fromSize: 0,
      fromAlpha: 0,
      fromFront: false,
      // this frame's draw position, set by queue()
      drawX: 0,
      drawY: 0,
      drawSize: 0,
      hidden: false // rests inside the centrepiece's clear box
    });
  }
  return shards;
}

/* Resting spot -> rx / ry, folding spots out of the quiet bands (shares of the
   box height) unless the shard is one of the few that stay; then flags the
   shard hidden if it would rest inside `clear` (shares of the box, or null). */
function place(s, quietTop, quietBottom, clear) {
  const lo = Math.min(0.45, Math.max(0, quietTop));
  const hi = Math.max(0.55, 1 - Math.max(0, quietBottom));
  s.rx = s.spot.x;
  s.ry = s.spot.y;
  if (!s.spot.keep && (s.spot.y < lo || s.spot.y > hi)) {
    s.ry = lo + s.spot.alt * (hi - lo);
  }
  s.hidden = Boolean(
    clear && s.rx > clear.left && s.rx < clear.right && s.ry > clear.top && s.ry < clear.bottom
  );
}

function readShare(computed, name) {
  const value = parseFloat(computed.getPropertyValue(name));
  return Number.isFinite(value) ? value : 0;
}

function cx(...names) {
  return names.filter(Boolean).join(" ");
}

/* ---------------------------------------------------------------- component -- */
function Shards({ className, children, ...rest }) {
  const wrapRef = useRef(null);
  const backRef = useRef(null);
  const frontRef = useRef(null);
  const contentRef = useRef(null);
  const [motionOk, setMotionOk] = useState(canAnimate);

  // Follow the OS setting live: the effect below re-runs and either starts the
  // loop or draws the one still frame.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const query = window.matchMedia(REDUCED_QUERY);
    const sync = () => setMotionOk(canAnimate());
    sync();
    if (query.addEventListener) {
      query.addEventListener("change", sync);
      return () => query.removeEventListener("change", sync);
    }
    if (query.addListener) {
      query.addListener(sync);
      return () => query.removeListener(sync);
    }
    return undefined;
  }, []);

  useEffect(() => {
    const wrap = wrapRef.current;
    const back = backRef.current;
    const front = frontRef.current;
    if (!wrap || !back || !front) {
      return undefined;
    }
    let backCtx = null;
    let frontCtx = null;
    try {
      backCtx = back.getContext("2d");
      frontCtx = front.getContext("2d");
    } catch (error) {
      backCtx = null;
    }
    if (!backCtx || !frontCtx) {
      return undefined;
    }

    const still = !motionOk;
    const jitter = mulberry32(SEED + 1);
    const root = document.documentElement;
    const projected = { x: 0, y: 0, z: 0, scale: 1 };
    const buckets = [];
    for (let b = 0; b < BUCKETS_PER_LAYER * 2; b += 1) {
      buckets.push([]);
    }

    let width = 0;
    let height = 0;
    let dpr = 1;
    let unit = 1;
    let radius = 1;
    let shards = [];
    let fills = [];
    let quietTop = 0;
    let quietBottom = 0;
    let clear = null; // the centrepiece's box + CLEAR_PAD, in shares of the box

    let raf = 0;
    let running = false;
    let onScreen = true;
    let last = 0;
    let time = 0; // seconds the loop has run (drives spin and drift)
    let introT = 0; // seconds since the preloader let go
    let burstAt = still ? 0 : -1; // introT at the burst; -1 = still an orb
    let burstWanted = false;

    // pointer: raw client coords in, box coords + smoothed speed out
    let seen = false;
    let clientX = 0;
    let clientY = 0;
    let pointerX = 0;
    let pointerY = 0;
    let pointerIn = false;
    let tracking = false; // false until the first sample after (re-)entry
    let speed = 0;
    let parX = 0;
    let parY = 0;

    const readStyle = () => {
      const computed = window.getComputedStyle(wrap);
      fills = COLOURS.map((name) => computed.getPropertyValue(name).trim());
      quietTop = readShare(computed, "--shards-quiet-top");
      quietBottom = readShare(computed, "--shards-quiet-bottom");
    };

    const restSize = (s) => s.size * unit * (0.75 + s.depth * 0.5);
    const restAlpha = (s) => (s.hidden ? 0 : s.alpha);
    const placeAll = () => {
      for (let i = 0; i < shards.length; i += 1) {
        place(shards[i], quietTop, quietBottom, clear);
      }
    };

    const readClear = () => {
      const piece = contentRef.current && contentRef.current.firstElementChild;
      const box = wrap.getBoundingClientRect();
      const r = piece ? piece.getBoundingClientRect() : null;
      if (!r || !r.width || !r.height || !box.width || !box.height) {
        clear = null;
        return;
      }
      const pad = CLEAR_PAD * unit;
      clear = {
        left: (r.left - box.left - pad) / box.width,
        right: (r.right - box.left + pad) / box.width,
        top: (r.top - box.top - pad) / box.height,
        bottom: (r.bottom - box.top + pad) / box.height
      };
    };

    const settle = (s) => {
      s.x = s.rx * width;
      s.y = s.ry * height;
      s.vx = 0;
      s.vy = 0;
      s.fromSize = restSize(s);
      s.fromAlpha = restAlpha(s);
      s.fromFront = s.front;
    };

    // Where shard `s` sits on the orb right now, into `projected`.
    const orbAt = (s) => {
      const turn = time * ORB_SPIN;
      let x = s.ox;
      let y = s.oy;
      let z = s.oz;
      let t = 0;
      if (s.ring) {
        // spin in the ring's own plane, then lay it over and roll it
        const a = turn * 1.4;
        t = x * Math.cos(a) + z * Math.sin(a);
        z = -x * Math.sin(a) + z * Math.cos(a);
        x = t;
        t = y * Math.cos(RING_TILT) - z * Math.sin(RING_TILT);
        z = y * Math.sin(RING_TILT) + z * Math.cos(RING_TILT);
        y = t;
        t = x * Math.cos(RING_ROLL) - y * Math.sin(RING_ROLL);
        y = x * Math.sin(RING_ROLL) + y * Math.cos(RING_ROLL);
        x = t;
      } else {
        t = x * Math.cos(turn) + z * Math.sin(turn);
        z = -x * Math.sin(turn) + z * Math.cos(turn);
        x = t;
        t = y * Math.cos(ORB_TILT) - z * Math.sin(ORB_TILT);
        z = y * Math.sin(ORB_TILT) + z * Math.cos(ORB_TILT);
        y = t;
      }
      const grow = easeOutCubic(introT / INTRO_S);
      const r = radius * (0.55 + 0.45 * grow);
      const persp = PERSPECTIVE / (PERSPECTIVE - z);
      projected.x = width / 2 + x * persp * r;
      projected.y = height / 2 + y * persp * r;
      projected.z = z;
      projected.scale = persp;
    };

    // Queue shard `s` at (x, y), `size` px, for its layer / colour / opacity.
    const queue = (s, x, y, size, alpha, onFront) => {
      const step = Math.round(Math.min(1, alpha) * ALPHA_STEPS);
      if (step <= 0 || size <= 0.1) {
        return;
      }
      s.drawX = x;
      s.drawY = y;
      s.drawSize = size;
      const layer = onFront ? BUCKETS_PER_LAYER : 0;
      buckets[layer + s.colour * (ALPHA_STEPS + 1) + step].push(s);
    };

    // Adds shard `s`'s outline to the current path, in device px.
    const outline = (ctx, s) => {
      const c = Math.cos(s.angle) * s.drawSize * dpr;
      const n = Math.sin(s.angle) * s.drawSize * dpr;
      const x = s.drawX * dpr;
      const y = s.drawY * dpr;
      const v = s.verts;
      ctx.moveTo(x + v[0] * c - v[1] * n, y + v[0] * n + v[1] * c);
      for (let k = 1; k < s.points; k += 1) {
        const vx = v[k * 2];
        const vy = v[k * 2 + 1];
        ctx.lineTo(x + vx * c - vy * n, y + vx * n + vy * c);
      }
      ctx.closePath();
    };

    const flush = () => {
      for (let b = 0; b < buckets.length; b += 1) {
        const list = buckets[b];
        if (!list.length) {
          continue;
        }
        const ctx = b >= BUCKETS_PER_LAYER ? frontCtx : backCtx;
        const inLayer = b % BUCKETS_PER_LAYER;
        const colour = Math.floor(inLayer / (ALPHA_STEPS + 1));
        ctx.globalAlpha = (inLayer % (ALPHA_STEPS + 1)) / ALPHA_STEPS;
        ctx.beginPath();
        for (let i = 0; i < list.length; i += 1) {
          outline(ctx, list[i]);
        }
        ctx.fillStyle = fills[colour];
        ctx.fill();
        if (colour === LIME) {
          // the ink edge, one CSS px
          ctx.lineWidth = dpr;
          ctx.strokeStyle = fills[INK];
          ctx.stroke();
        }
        list.length = 0;
      }
    };

    const draw = () => {
      backCtx.setTransform(1, 0, 0, 1, 0, 0);
      backCtx.globalAlpha = 1;
      backCtx.clearRect(0, 0, back.width, back.height);
      frontCtx.setTransform(1, 0, 0, 1, 0, 0);
      frontCtx.globalAlpha = 1;
      frontCtx.clearRect(0, 0, front.width, front.height);

      if (burstAt < 0) {
        const grow = easeOutCubic(introT / INTRO_S);
        if (grow <= 0) {
          return;
        }
        for (let i = 0; i < shards.length; i += 1) {
          const s = shards[i];
          orbAt(s);
          // the far side of the orb recedes
          const near = (projected.z + 1) / 2;
          const alpha = s.alpha * grow * (0.4 + 0.6 * Math.min(1, Math.max(0, near)));
          const size = s.size * unit * projected.scale * ORB_SHARD_SCALE;
          queue(s, projected.x, projected.y, size, alpha, projected.z > 0);
        }
        flush();
        return;
      }

      const k = easeOutCubic((introT - burstAt) / SETTLE_S);
      for (let i = 0; i < shards.length; i += 1) {
        const s = shards[i];
        const size = s.fromSize + (restSize(s) - s.fromSize) * k;
        const alpha = s.fromAlpha + (restAlpha(s) - s.fromAlpha) * k;
        queue(s, s.x, s.y, size, alpha, k < 0.5 ? s.fromFront : s.front);
      }
      flush();
    };

    const burst = () => {
      burstAt = introT;
      const midX = width / 2;
      const midY = height / 2;
      for (let i = 0; i < shards.length; i += 1) {
        const s = shards[i];
        orbAt(s);
        s.x = projected.x;
        s.y = projected.y;
        s.fromSize = s.size * unit * projected.scale * ORB_SHARD_SCALE;
        s.fromAlpha = s.alpha * (0.4 + 0.6 * Math.min(1, Math.max(0, (projected.z + 1) / 2)));
        s.fromFront = projected.z > 0;
      }
      // Hand the resting spots out by angle around the centre, so every shard
      // flies OUTWARD to one rather than criss-crossing the box.
      const angleOf = (x, y) => Math.atan2(y - midY, x - midX);
      const byShard = shards.slice().sort((a, b) => angleOf(a.x, a.y) - angleOf(b.x, b.y));
      const spots = shards
        .map((s) => ({ rx: s.rx, ry: s.ry, spot: s.spot }))
        .sort((a, b) => angleOf(a.rx * width, a.ry * height) - angleOf(b.rx * width, b.ry * height));
      byShard.forEach((s, index) => {
        s.spot = spots[index].spot;
      });
      placeAll();
      for (let i = 0; i < shards.length; i += 1) {
        const s = shards[i];
        s.vx = (s.rx * width - s.x) * KICK + (jitter() - 0.5) * 240 * unit;
        s.vy = (s.ry * height - s.y) * KICK + (jitter() - 0.5) * 240 * unit;
      }
    };

    const step = (dt) => {
      if (seen) {
        const rect = wrap.getBoundingClientRect();
        const nextX = clientX - rect.left;
        const nextY = clientY - rect.top;
        if (!tracking) {
          // first sample: no "jump" from wherever the pointer last was
          pointerX = nextX;
          pointerY = nextY;
          tracking = true;
        }
        const moved = Math.sqrt((nextX - pointerX) * (nextX - pointerX) + (nextY - pointerY) * (nextY - pointerY));
        speed += (moved / Math.max(dt, 0.001) - speed) * (1 - Math.exp(-dt * 10));
        pointerX = nextX;
        pointerY = nextY;
        pointerIn = nextX >= 0 && nextY >= 0 && nextX <= width && nextY <= height;
      }
      const parEase = 1 - Math.exp(-dt * 3);
      parX += ((pointerIn ? (pointerX - width / 2) * PARALLAX : 0) - parX) * parEase;
      parY += ((pointerIn ? (pointerY - height / 2) * PARALLAX : 0) - parY) * parEase;

      const since = introT - burstAt;
      const kickLeft = Math.exp(-since * 2.2);
      const reach = PUSH_REACH * unit;
      const push = PUSH_BASE + Math.min(speed, 3000) * PUSH_SPEED;
      for (let i = 0; i < shards.length; i += 1) {
        const s = shards[i];
        const lean = (s.depth - 0.5) * 2;
        const tx = s.rx * width + Math.sin(time * s.w1 + s.p1) * s.amp * unit - parX * lean;
        const ty = s.ry * height + Math.cos(time * s.w2 + s.p2) * s.amp * unit * 0.8 - parY * lean;
        let ax = SPRING * (tx - s.x) - DAMPING * s.vx;
        let ay = SPRING * (ty - s.y) - DAMPING * s.vy;
        if (pointerIn) {
          const dx = s.x - pointerX;
          const dy = s.y - pointerY;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < reach && d > 0.5) {
            const f = 1 - d / reach;
            ax += (dx / d) * f * f * push;
            ay += (dy / d) * f * f * push;
          }
        }
        s.vx += ax * dt;
        s.vy += ay * dt;
        s.x += s.vx * dt;
        s.y += s.vy * dt;
        s.angle += (s.spin + s.kick * kickLeft) * dt;
      }
    };

    const tick = (now) => {
      if (!running) {
        return;
      }
      const dt = last ? Math.min((now - last) / 1000, 0.05) : 0.016;
      last = now;
      time += dt;

      const held = PRELOAD_CLASSES.some((name) => root.classList.contains(name));
      if (!held) {
        introT += dt;
      }

      if (burstAt < 0) {
        if (introT >= MIN_ORB_S && (burstWanted || introT >= AUTO_BURST_S)) {
          burst();
        } else {
          // the orb's own spin
          for (let i = 0; i < shards.length; i += 1) {
            shards[i].angle += shards[i].spin * dt;
          }
        }
      }
      if (burstAt >= 0) {
        step(dt);
      }
      draw();
      raf = window.requestAnimationFrame(tick);
    };

    const start = () => {
      if (still || running || !onScreen || document.hidden) {
        return;
      }
      running = true;
      last = 0;
      raf = window.requestAnimationFrame(tick);
    };

    const stop = () => {
      running = false;
      if (raf) {
        window.cancelAnimationFrame(raf);
        raf = 0;
      }
    };

    const measure = () => {
      const nextWidth = wrap.clientWidth;
      const nextHeight = wrap.clientHeight;
      if (!nextWidth || !nextHeight) {
        return;
      }
      const nextDpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      const count = countFor(nextWidth);
      unit = Math.min(1.2, Math.max(0.62, Math.min(nextWidth, nextHeight) / 900));
      readStyle();
      readClear();
      if (count !== shards.length) {
        shards = buildShards(count);
        placeAll();
        width = nextWidth;
        height = nextHeight;
        if (burstAt >= 0) {
          shards.forEach(settle);
        }
      } else if (width && burstAt >= 0 && (nextWidth !== width || nextHeight !== height)) {
        // keep the field where it was, relative to the box
        const sx = nextWidth / width;
        const sy = nextHeight / height;
        shards.forEach((s) => {
          s.x *= sx;
          s.y *= sy;
        });
      }
      width = nextWidth;
      height = nextHeight;
      dpr = nextDpr;
      back.width = Math.round(width * dpr);
      back.height = Math.round(height * dpr);
      front.width = back.width;
      front.height = back.height;
      // the ring (RING_RADIUS x this) just fits a phone's width
      radius = Math.min(width * 0.3, height * 0.25);
      // a breakpoint may have moved the quiet bands or the centrepiece
      placeAll();
      if (still) {
        shards.forEach(settle);
        introT = SETTLE_S;
      }
      draw();
    };

    const wantBurst = () => {
      burstWanted = true;
    };
    const onMouseMove = (event) => {
      clientX = event.clientX;
      clientY = event.clientY;
      seen = true;
      wantBurst();
    };
    const onTouch = (event) => {
      const touch = event.touches && event.touches[0];
      if (!touch) {
        return;
      }
      clientX = touch.clientX;
      clientY = touch.clientY;
      seen = true;
      wantBurst();
    };
    const onScroll = () => {
      if (window.scrollY > 8) {
        wantBurst();
      }
    };
    const onPointerGone = () => {
      seen = false;
      pointerIn = false;
      tracking = false;
      speed = 0;
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    };

    const passive = { passive: true };
    if (!still) {
      window.addEventListener("mousemove", onMouseMove, passive);
      window.addEventListener("touchstart", onTouch, passive);
      window.addEventListener("touchmove", onTouch, passive);
      window.addEventListener("scroll", onScroll, passive);
      window.addEventListener("blur", onPointerGone);
      root.addEventListener("mouseleave", onPointerGone);
      document.addEventListener("visibilitychange", onVisibility);
    }

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(wrap);
      // the centrepiece reflows on its own (web fonts landing, a copy change)
      const piece = contentRef.current && contentRef.current.firstElementChild;
      if (piece) {
        resizeObserver.observe(piece);
      }
    } else {
      window.addEventListener("resize", measure);
    }

    let intersectionObserver = null;
    if (!still && typeof IntersectionObserver !== "undefined") {
      intersectionObserver = new IntersectionObserver(
        (entries) => {
          onScreen = entries[entries.length - 1].isIntersecting;
          if (onScreen) {
            start();
          } else {
            stop();
          }
        },
        { rootMargin: "-1px 0px -1px 0px" }
      );
      intersectionObserver.observe(wrap);
    }

    measure();
    start();

    return () => {
      stop();
      if (!still) {
        window.removeEventListener("mousemove", onMouseMove, passive);
        window.removeEventListener("touchstart", onTouch, passive);
        window.removeEventListener("touchmove", onTouch, passive);
        window.removeEventListener("scroll", onScroll, passive);
        window.removeEventListener("blur", onPointerGone);
        root.removeEventListener("mouseleave", onPointerGone);
        document.removeEventListener("visibilitychange", onVisibility);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener("resize", measure);
      }
      if (intersectionObserver) {
        intersectionObserver.disconnect();
      }
    };
  }, [motionOk]);

  return (
    <div {...rest} ref={wrapRef} className={cx(styles.root, className)}>
      <canvas ref={backRef} className={cx(styles.canvas, styles.back)} aria-hidden="true" />
      <div ref={contentRef} className={styles.content}>
        {children}
      </div>
      <canvas ref={frontRef} className={cx(styles.canvas, styles.front)} aria-hidden="true" />
    </div>
  );
}

export default Shards;
